import { useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  COHERENCE_STATUTS,
  COHERENCE_STATUT_LABELS,
  type CoherenceQueryInput,
  type CoherenceStatut,
} from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';

function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

function formaterDate(iso: string | null): string {
  if (!iso) return '—';
  const [annee, mois, jour] = iso.split('-');
  return jour && mois && annee ? `${jour}/${mois}/${annee}` : iso;
}

const CLASSE_STATUT: Record<CoherenceStatut, string> = {
  CONFORME: 'etiquette etiquette--actif',
  CONFORME_FAMILLE: 'etiquette etiquette--actif',
  NON_DECLARE: 'etiquette etiquette--attente',
  SANS_TABLE: 'etiquette',
};

/**
 * Cohérence FAB ↔ tables de copacking (règle 15).
 *
 * Contrôle **informatif** : les fabrications font foi opérationnellement, la
 * table reste le référentiel déclaré. Consommer un jumeau du composant déclaré
 * est conforme — c'est le même article physique.
 */
export function CopackingCoherencePage(): ReactElement {
  const [statuts, setStatuts] = useState<CoherenceStatut[]>(['NON_DECLARE']);
  const [recherche, setRecherche] = useState('');

  const filtres: CoherenceQueryInput = useMemo(
    () => ({
      statuts: statuts.length > 0 ? statuts : [...COHERENCE_STATUTS],
      ...(recherche.trim() ? { search: recherche.trim() } : {}),
    }),
    [statuts, recherche],
  );

  const rapport = useQuery({
    queryKey: ['copacking', 'coherence', filtres],
    queryFn: () => api.coherenceCopacking(filtres),
  });

  const basculer = (statut: CoherenceStatut): void => {
    setStatuts((actuels) =>
      actuels.includes(statut) ? actuels.filter((s) => s !== statut) : [...actuels, statut],
    );
  };

  return (
    <>
      <h1>Cohérence copacking</h1>

      <p className="bandeau-avertissement" role="note">
        <strong>Contrôle informatif</strong>
        <span>
          Les fabrications FAB font foi opérationnellement ; les tables Copacking et Nomenclature
          restent le référentiel déclaré. La conformité se juge au niveau <strong>famille</strong> :
          consommer un jumeau du composant déclaré, c&apos;est consommer le même article physique.
          Rien ici ne bloque un import ni ne modifie un écart.
        </span>
      </p>

      <div className="carte">
        <div className="barre-actions barre-actions--titre">
          <div className="barre-actions">
            <label className="champ" htmlFor="coherence-recherche">
              <span>Rechercher</span>
              <input
                id="coherence-recherche"
                type="search"
                placeholder="Composé, composant ou référence FAB"
                value={recherche}
                onChange={(event) => setRecherche(event.target.value)}
              />
            </label>
            <fieldset className="champ champ--large">
              <legend>Statuts affichés</legend>
              <div className="cases">
                {COHERENCE_STATUTS.map((statut) => (
                  <label key={statut} className="case">
                    <input
                      type="checkbox"
                      checked={statuts.includes(statut)}
                      onChange={() => basculer(statut)}
                    />
                    <span>{COHERENCE_STATUT_LABELS[statut]}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
          <div className="barre-actions__boutons">
            <a className="bouton bouton--secondaire" href={api.coherenceExportUrl(filtres)}>
              Exporter en Excel
            </a>
          </div>
        </div>

        {rapport.isPending && <p className="chargement">Analyse des fabrications…</p>}
        {rapport.isError && <p className="alerte alerte--erreur">{messageErreur(rapport.error)}</p>}

        {rapport.data && (
          <>
            <div className="totaux">
              <div>
                <span>Fabrications analysées</span>
                <strong>{rapport.data.fabrications.toLocaleString('fr-FR')}</strong>
              </div>
              {COHERENCE_STATUTS.map((statut) => (
                <div key={statut}>
                  <span>{COHERENCE_STATUT_LABELS[statut]}</span>
                  <strong
                    className={
                      statut === 'NON_DECLARE' && rapport.data.totaux[statut] > 0
                        ? 'nombre nombre--alerte'
                        : 'nombre'
                    }
                  >
                    {rapport.data.totaux[statut].toLocaleString('fr-FR')}
                  </strong>
                </div>
              ))}
            </div>

            {rapport.data.lignes.length === 0 ? (
              <p className="alerte alerte--info" role="status">
                Aucune ligne sur ce filtre. Aucun lien non déclaré ne reste à instruire sur les
                fabrications analysées.
              </p>
            ) : (
              <div className="tableau-defilant">
                <table className="tableau">
                  <thead>
                    <tr>
                      <th scope="col">Référence FAB</th>
                      <th scope="col">Jour</th>
                      <th scope="col">Composé</th>
                      <th scope="col">Composant consommé</th>
                      <th scope="col">UVC</th>
                      <th scope="col">Statut</th>
                      <th scope="col">Déclaré par la table</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rapport.data.lignes.map((ligne) => (
                      <tr key={`${ligne.reference}-${ligne.composant}`}>
                        <td>{ligne.reference}</td>
                        <td>{formaterDate(ligne.jour)}</td>
                        <td>
                          {ligne.compose}
                          {ligne.composeDesignation && (
                            <div className="sous-titre">{ligne.composeDesignation}</div>
                          )}
                        </td>
                        <td>
                          {ligne.composant}
                          {ligne.composantDesignation && (
                            <div className="sous-titre">{ligne.composantDesignation}</div>
                          )}
                        </td>
                        <td className="droite">{formaterNombre(ligne.uvcComposant)}</td>
                        <td>
                          <span className={CLASSE_STATUT[ligne.statut]}>
                            {COHERENCE_STATUT_LABELS[ligne.statut]}
                          </span>
                        </td>
                        <td>
                          {ligne.composantsDeclares.length === 0
                            ? '— rien de déclaré'
                            : ligne.composantsDeclares.join(' / ')}
                          {ligne.declareEquivalent && (
                            <div className="sous-titre">
                              jumeau de {ligne.declareEquivalent}, même famille validée
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {rapport.data && rapport.data.composesDeclares.length > 0 && (
        <div className="carte">
          <h2>Anomalies d&apos;instruction — composés déclarés en comptabilité matières</h2>
          <p className="sous-titre">
            L&apos;expédition d&apos;un composé est déclarée <strong>sous le code composant</strong>{' '}
            (règle 15) : la douane ne connaît que le père. Un composé qui porte malgré tout des
            lignes GEJNM a été instruit à tort ; le cas est à traiter avec KN, il ne se corrige pas
            depuis l&apos;outil.
          </p>
          <div className="tableau-defilant">
            <table className="tableau">
              <thead>
                <tr>
                  <th scope="col">Composé</th>
                  <th scope="col">Libellé</th>
                  <th scope="col">Lignes GEJNM</th>
                  <th scope="col">UVC entrées</th>
                  <th scope="col">UVC sorties</th>
                </tr>
              </thead>
              <tbody>
                {rapport.data.composesDeclares.map((anomalie) => (
                  <tr key={anomalie.compose}>
                    <td>{anomalie.compose}</td>
                    <td>{anomalie.designation ?? '(sans désignation au référentiel)'}</td>
                    <td className="droite nombre nombre--alerte">
                      {anomalie.lignes.toLocaleString('fr-FR')}
                    </td>
                    <td className="droite">{formaterNombre(anomalie.uvcEntrees)}</td>
                    <td className="droite">{formaterNombre(anomalie.uvcSorties)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
